import {faCircleInfo} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Text} from '@rdcomponents/text/text';
import {Flex, Form, FormInstance, Input, Select, Tooltip} from 'antd';
import {NamePath} from 'antd/es/form/interface';
import {useEffect} from 'react';

import {spacer_xs} from '../../../../css/var-export.scss';
import {Locale} from '../../../../provider/locale/localization';
import type {LangKeyType} from '../../../../provider/locale/translation/type';
import {useCompany} from '../../../../service/company/company-hook';
import {useFormRules} from '../../../../service/form/form-rules-hook';
import {useReviewCustomBrands} from '../../../../service/reviews/reviews-custom-brands';
import {ReviewCustomCompanyType} from '../../../../service/reviews/reviews-custom-compamies';
import {CompanySelect} from '../company-select/company-select';

import {ReviewBrandCompanyScopeFieldNamesType, ReviewEntityScopeEnum} from './review-brand-company-scope-card.types';
import * as styles from './review-brand-company-scope-card.module.scss';

type PropsType = {
    form: FormInstance;
    fieldNames: ReviewBrandCompanyScopeFieldNamesType;
    initialCompany?: ReviewCustomCompanyType | null;
};

export function ReviewBrandCompanyScopeCard(props: PropsType): JSX.Element {
    const {form, fieldNames, initialCompany} = props;

    const {requiredFieldRule} = useFormRules();
    const {data: brands, isFetching: isBrandsLoading} = useReviewCustomBrands();

    const scope =
        (Form.useWatch(fieldNames.entityScope, form) as ReviewEntityScopeEnum | undefined) ??
        ReviewEntityScopeEnum.brand;

    const isCompanyScope = scope === ReviewEntityScopeEnum.company;

    const selectedCompanyIdRaw = Form.useWatch(fieldNames.company, form);
    const selectedCompanyId = selectedCompanyIdRaw ? Number(selectedCompanyIdRaw) : null;

    const {result: selectedCompany} = useCompany(selectedCompanyId || null);

    useEffect(() => {
        if (selectedCompany) {
            form.setFieldValue(fieldNames.brand, selectedCompany.brand.id);
        }
    }, [fieldNames.brand, form, selectedCompany]);

    function handleScopeChange(nextScope: ReviewEntityScopeEnum): void {
        form.setFieldValue(fieldNames.entityScope, nextScope);

        form.resetFields([fieldNames.brand, fieldNames.company]);
    }

    useEffect(() => {
        const isBrandScope = scope === ReviewEntityScopeEnum.brand;

        if (!isBrandScope || !brands || brands.length !== 1) {
            return;
        }

        const [firstBrand] = brands;

        form.setFieldsValue({
            [fieldNames.brand]: firstBrand?.id,
        });
    }, [brands, fieldNames.brand, form, scope]);

    function renderScopeTab(nextScope: ReviewEntityScopeEnum, labelKey: LangKeyType): JSX.Element {
        const isActive = scope === nextScope;

        return (
            <button
                className={`${styles.ReviewBrandCompanyScopeCard_tab} ${
                    isActive ? styles.ReviewBrandCompanyScopeCard_tabActive : ''
                }`}
                onClick={() => handleScopeChange(nextScope)}
                type="button"
            >
                <Text inheritColor stringKey={labelKey} />
            </button>
        );
    }

    return (
        <>
            <Form.Item hidden name={fieldNames.entityScope as NamePath}>
                <Input />
            </Form.Item>

            {isCompanyScope && (
                <Form.Item hidden name={fieldNames.brand as NamePath} preserve>
                    <Input />
                </Form.Item>
            )}

            <Form.Item
                className={styles.ReviewBrandCompanyScopeCard_formItemRoot}
                colon={false}
                label={
                    <Flex align="center" gap={spacer_xs} wrap="wrap">
                        <Text large lighter stringKey="REVIEW_IMPORT__REVIEW_TYPE_LABEL" />
                        <Tooltip title={<Locale stringKey="REVIEW_IMPORT__REVIEW_TYPE_TOOLTIP" />}>
                            <span className={styles.ReviewBrandCompanyScopeCard_infoIconWrap}>
                                <FontAwesomeIcon icon={faCircleInfo} />
                            </span>
                        </Tooltip>
                    </Flex>
                }
                required
            >
                <div className={styles.ReviewBrandCompanyScopeCard_panel}>
                    <div className={styles.ReviewBrandCompanyScopeCard_tabBar} role="tablist">
                        {renderScopeTab(ReviewEntityScopeEnum.brand, 'REVIEW__FORM__FIELD_BRAND')}
                        {renderScopeTab(ReviewEntityScopeEnum.company, 'REVIEW__FORM__FIELD_COMPANY')}
                    </div>
                    <div className={styles.ReviewBrandCompanyScopeCard_selectArea}>
                        {scope === ReviewEntityScopeEnum.brand ? (
                            <div className={styles.ReviewBrandCompanyScopeCard_nestedItem}>
                                <Form.Item
                                    name={fieldNames.brand as NamePath}
                                    noStyle
                                    preserve={false}
                                    rules={[requiredFieldRule]}
                                >
                                    <Select<number>
                                        disabled={isBrandsLoading}
                                        getPopupContainer={(triggerNode) => triggerNode.ownerDocument.body}
                                        loading={isBrandsLoading}
                                        placeholder={
                                            <Text large lightest stringKey="REVIEW__FORM__FIELD_BRAND__PLACEHOLDER" />
                                        }
                                        size="large"
                                    >
                                        {brands?.map((brand) => (
                                            <Select.Option key={brand.id} label={brand.name} value={brand.id}>
                                                <Text large lighter>
                                                    {brand.name}
                                                </Text>
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </div>
                        ) : (
                            <div className={styles.ReviewBrandCompanyScopeCard_nestedItem}>
                                <Form.Item
                                    name={fieldNames.company as NamePath}
                                    noStyle
                                    preserve={false}
                                    rules={[requiredFieldRule]}
                                >
                                    <CompanySelect
                                        brandId={null}
                                        getPopupContainer={(triggerNode) => triggerNode.ownerDocument.body}
                                        initialCompany={initialCompany}
                                        size="large"
                                    />
                                </Form.Item>
                            </div>
                        )}
                    </div>
                </div>
            </Form.Item>
        </>
    );
}
